// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT - ReferralSection
// Section parrainage intégrée dans le CreditsModal
// Affiche le code, les stats et les boutons de partage
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
  Gift, Copy, Check, Users, Coins, Share2, 
  Loader2, ExternalLink, MessageCircle, Mail,
  Trophy, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour récupérer l'userId
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  const userId = localStorage.getItem('userId');
  
  if (userId) return userId;
  
  if (ecosystemUser) {
    try {
      const parsed = JSON.parse(ecosystemUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (buyerUser) {
    try {
      const parsed = JSON.parse(buyerUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  if (clientUser) {
    try {
      const parsed = JSON.parse(clientUser);
      return parsed.id || parsed.user_id;
    } catch(e) {}
  }
  
  return null;
};

export default function ReferralSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  const userId = getUserId();
  
  // Récupérer les stats de parrainage
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/referral/stats/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Error fetching referral stats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [userId]);
  
  // Copier le code
  const copyCode = () => {
    if (!stats?.code) return;
    
    navigator.clipboard.writeText(stats.code);
    setCopied(true);
    toast.success('Code copié !');
    
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Copier le lien complet
  const copyLink = () => {
    if (!stats?.code) return;
    
    const link = `${window.location.origin}/reseau/inscription?parrain=${stats.code}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };
  
  // Partager sur WhatsApp
  const shareWhatsApp = () => {
    if (!stats?.code) return;
    
    const text = `Rejoins AbracadaWorld avec mon code ${stats.code} et reçois ${stats.referral_welcome} crédits bonus ! 🎁\n${window.location.origin}/reseau/inscription?parrain=${stats.code}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };
  
  // Partager par email
  const shareEmail = () => {
    if (!stats?.code) return;
    
    const subject = "Rejoins-moi sur AbracadaWorld !";
    const body = `Salut !\n\nJe t'invite à rejoindre AbracadaWorld, la plateforme qui connecte artisans et particuliers.\n\nUtilise mon code parrain : ${stats.code}\nTu recevras ${stats.referral_welcome} crédits bonus à l'inscription !\n\nInscris-toi ici : ${window.location.origin}/reseau/inscription?parrain=${stats.code}\n\nÀ bientôt !`;
    
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-5">
      {/* Header explicatif */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Parraine tes amis, gagne des crédits !</h3>
            <p className="text-sm text-gray-600 mt-1">
              Pour chaque ami inscrit avec ton code, tu gagnes <strong className="text-purple-600">{stats?.sponsor_reward || 50} crédits</strong> et ton ami reçoit <strong className="text-pink-600">{stats?.referral_welcome || 25} crédits</strong> de bienvenue.
            </p>
          </div>
        </div>
      </div>
      
      {/* Code de parrainage */}
      <div className="bg-gray-50 rounded-xl p-5">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Ton code parrain
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-center">
            <span className="text-2xl font-bold tracking-widest text-[#1E3A5F]">
              {stats?.code || '--------'}
            </span>
          </div>
          <button
            onClick={copyCode}
            className={`
              p-3 rounded-lg transition-all
              ${copied 
                ? 'bg-emerald-500 text-white' 
                : 'bg-[#1E3A5F] text-white hover:bg-[#2d5a8a]'
              }
            `}
            title="Copier le code"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-600 mb-1">
            <Users className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats?.referrals_count || 0}</span>
          </div>
          <p className="text-xs text-gray-500">Filleuls inscrits</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-500 mb-1">
            <Coins className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats?.credits_earned || 0}</span>
          </div>
          <p className="text-xs text-gray-500">Crédits gagnés</p>
        </div>
      </div>
      
      {/* Boutons de partage */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Partager ton lien
        </p>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={shareWhatsApp}
            className="flex items-center justify-center gap-2 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-medium"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </button>
          <button
            onClick={shareEmail}
            className="flex items-center justify-center gap-2 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            <Mail className="w-4 h-4" />
            Email
          </button>
          <button
            onClick={copyLink}
            className="flex items-center justify-center gap-2 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            Lien
          </button>
        </div>
      </div>
      
      {/* Liste des filleuls récents */}
      {stats?.recent_referrals?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Tes filleuls récents
          </p>
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            {stats.recent_referrals.map((ref, idx) => (
              <div key={ref.referral_user_id || idx} className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-purple-600">
                      {ref.referral_name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{ref.referral_name}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(ref.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-emerald-600 font-semibold text-sm">
                  <Sparkles className="w-4 h-4" />
                  +{ref.credits_awarded}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Message d'encouragement si pas encore de filleuls */}
      {(!stats?.recent_referrals || stats.recent_referrals.length === 0) && (
        <div className="text-center py-4 text-gray-500">
          <Trophy className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Tu n'as pas encore de filleuls</p>
          <p className="text-xs text-gray-400 mt-1">Partage ton code pour commencer à gagner !</p>
        </div>
      )}
    </div>
  );
}
